import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/system";

import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ChatRoomUI from "../ChatRoom/ChatRoom";
import MessageSection from "../MessageSection/MessageSection";
import { getAllChatroom, } from "../../../../services/chatService";

// Memoize styled components
const StyledContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  height: "100vh",
  background: "#f2f4f5",
  borderRadius: 10,
  gap: 20,
  padding: 20,
  color: "#000"
}));

// Memoized ChatRoomUI wrapper component to control rerenders
const MemoizedChatRoomUI = React.memo(({ contacts, setContacts, onChange, selectedChatroom, handleContactClick }) => (
  <ChatRoomUI 
    contacts={contacts} 
    setContacts={setContacts} 
    onChange={onChange} 
    selectedChatroom={selectedChatroom} 
    handleContactClick={handleContactClick} 
  />
), (prevProps, nextProps) => {
  // Only rerender if selected chatroom changes or if contacts change
  if (prevProps.selectedChatroom?.id !== nextProps.selectedChatroom?.id) {
    return false;
  }
  
  if (prevProps.contacts.length !== nextProps.contacts.length) {
    return false;
  }
  
  // Check if any contact's last message has changed
  for (let i = 0; i < prevProps.contacts.length; i++) {
    if (prevProps.contacts[i].lastMessage !== nextProps.contacts[i].lastMessage) {
      return false; // Last message changed, need to rerender
    }
  }
  
  return true;
});

// Memoized MessageSection wrapper component to control rerenders
const MemoizedMessageSection = React.memo(({ recievedMsg, selectedChatroom, socket, setSelectedChatroom, onMessageSent }) => (
  <MessageSection 
    recievedMsg={recievedMsg} 
    selectedChatroom={selectedChatroom} 
    socket={socket} 
    setSelectedChatroom={setSelectedChatroom}
    onMessageSent={onMessageSent}
  />
), (prevProps, nextProps) => {
  // Compare only the important properties to prevent unnecessary rerenders
  return (
    prevProps.selectedChatroom?.id === nextProps.selectedChatroom?.id &&
    prevProps.recievedMsg === nextProps.recievedMsg
  );
});

// Wrap the component with React.memo to prevent unnecessary rerenders
const ChatUI = React.memo(({ socketInstance, contacts, setContacts, onChange, recievedMsg, onMessageSent }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedChatroom, setSelectedChatroom] = useState(null);
  const user = useSelector((state) => state.user.user);
  const { t } = useTranslation(); // Hook to get translations

  const location = useLocation();
  const { employerId, jobSeekerId } = location.state || {};

  // Memoize updateMedia function to prevent recreation on each render
  const updateMedia = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  // Handle window resize
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  }, [updateMedia]);

  // Set selected chatroom based on URL params
  useEffect(() => {
    if (!contacts.length) return;
    
    const data = contacts.filter((e) => e.chatroom.id === jobSeekerId || e.chatroom.id === employerId);
    if (data?.length > 0 && selectedChatroom === null) {
      setSelectedChatroom(data[0]);
    }
  }, [contacts, employerId, jobSeekerId, selectedChatroom]);

  // Memoize handleContactClick to prevent recreation on each render
  const handleContactClick = useCallback((contact) => {
    setSelectedChatroom(contact); // Set the selected chatroom
    socketInstance.emit('joinRoom', contact.id); // Join room based on chatroom ID
  }, [socketInstance]);

  // Only fetch chatrooms when needed
  useEffect(() => {
    const fetchChatroom = async () => {
      if (!user?.id || !user?.account_type) return;
      
      try {
        const getAllChatroomResponse = await getAllChatroom(user.account_type, user.id);
        const getAllChatroomResponseCopy = JSON.parse(JSON.stringify(getAllChatroomResponse));
        const transformedContacts = getAllChatroomResponseCopy.map(chatroom => {
          return ({
            id: chatroom?.chatroom?.id || chatroom?.id,
            name: chatroom?.company_name ? chatroom?.company_name : (chatroom.first_name && chatroom.last_name)
              ? `${chatroom.first_name} ${chatroom.last_name}`
              : chatroom.email?.split('@')[0],
            avatar: chatroom?.profile_image,
            lastMessage: chatroom?.lastMessage?.messageContent || 'no message',
            chatroom: { ...chatroom },
          });
        });
        setContacts(transformedContacts);
      } catch (error) {
        console.error('Error fetching chatrooms:', error);
      }
    };

    if (contacts.length === 0) {
      fetchChatroom();
    }
  }, [contacts.length, setContacts, user?.account_type, user?.id]);

  // Memoize state and props to prevent unnecessary rerenders
  const memoizedContacts = useMemo(() => contacts, [contacts]);
  const memoizedSelectedChatroom = useMemo(() => selectedChatroom, [selectedChatroom]);
  const memoizedHandleContactClick = useMemo(() => handleContactClick, [handleContactClick]);
  const memoizedSetSelectedChatroom = useMemo(() => setSelectedChatroom, []);

  return (
    <StyledContainer>
      {isMobile ? (
        !selectedChatroom && (
          <MemoizedChatRoomUI 
            contacts={memoizedContacts} 
            setContacts={setContacts} 
            onChange={onChange} 
            selectedChatroom={memoizedSelectedChatroom} 
            handleContactClick={memoizedHandleContactClick} 
          />
        )
      ) : (
        <MemoizedChatRoomUI 
          contacts={memoizedContacts} 
          setContacts={setContacts} 
          onChange={onChange} 
          selectedChatroom={memoizedSelectedChatroom} 
          handleContactClick={memoizedHandleContactClick} 
        />
      )}
      
      {!isMobile ? (
        <MemoizedMessageSection 
          recievedMsg={recievedMsg} 
          selectedChatroom={memoizedSelectedChatroom} 
          socket={socketInstance} 
          onMessageSent={onMessageSent}
          setSelectedChatroom={memoizedSetSelectedChatroom}
        />
      ) : (
        selectedChatroom && (
          <MemoizedMessageSection 
            recievedMsg={recievedMsg} 
            socket={socketInstance} 
            selectedChatroom={memoizedSelectedChatroom} 
            setSelectedChatroom={memoizedSetSelectedChatroom} 
            onMessageSent={onMessageSent}
          />
        )
      )}
    </StyledContainer>
  );
}, (prevProps, nextProps) => {
  // Only rerender when necessary
  // Check if contacts length changed
  if (prevProps.contacts.length !== nextProps.contacts.length) {
    return false;
  }
  
  // Check if socket instance changed
  if (prevProps.socketInstance !== nextProps.socketInstance) {
    return false;
  }
  
  // Check if received message changed
  if (prevProps.recievedMsg !== nextProps.recievedMsg) {
    return false;
  }
  
  // Check for last message updates
  for (let i = 0; i < prevProps.contacts.length; i++) {
    if (prevProps.contacts[i].lastMessage !== nextProps.contacts[i].lastMessage) {
      return false;
    }
  }
  
  return true;
});

export default ChatUI;
